import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from 'antd';

interface OrderCodeSelectionHeaderProps {
  /** Số đơn đang tick chọn — hiển thị trên badge và dùng làm điểm neo. */
  selectedCount: number;
  /** Nội dung bảng hành động hàng loạt. */
  panel: React.ReactNode;
  /** Đăng ký hàm tạm ẩn popup mà không làm thay đổi selection hoặc dựng lại bảng. */
  registerClosePanel?: (close: () => void) => void;
}

/** Khoảng hở giữa mép phải cột và mép trái panel. */
const PANEL_GAP = 6;
/** Bề rộng ngoài của panel: nội dung 230px (Ecom `BatchActionPanel`, Đơn hàng `PosBatchActionPanel`) + padding 10px 2 bên. */
const PANEL_OUTER_WIDTH = 250;
/**
 * Dưới Modal của antd (1000): panel luôn mở nên hộp thoại mở từ panel (Xác nhận, Huỷ, Gán
 * thẻ…) phải phủ lên nó. Mặc định Popover là 1030 — đè lên cả lớp nền mờ của hộp thoại.
 */
const PANEL_Z_INDEX = 999;

/**
 * Tiêu đề cột "Mã đơn hàng" khi đang có đơn được chọn: một badge đếm số đơn dùng
 * làm điểm neo cho bảng hành động hàng loạt (thay Modal 720px trước đây).
 *
 * Bảng luôn mở khi còn đơn được chọn — click ra ngoài (kể cả tick thêm đơn) KHÔNG đóng nó
 * (TCR-2439: `trigger={['click']}` từng làm tick đơn thứ 2 đóng panel, mất nút Xác nhận hàng
 * loạt). Nơi dùng vẫn có thể tạm ẩn qua `registerClosePanel` (Ecom ẩn trong lúc "Đi đơn");
 * panel tự hiện lại khi số đơn chọn đổi, hoặc khi bấm badge.
 *
 * antd render tiêu đề cột HAI lần khi bảng có scroll: một lần ở header thật và
 * một lần ở `.ant-table-measure-row` ẩn dùng để đo bề rộng cột. Popover đẩy nội
 * dung ra portal ở `document.body` nên bản ở measure row cũng sẽ dựng thêm một
 * bảng hành động nữa, nổi sai chỗ. Vì vậy chỉ bản nằm ngoài measure row mới bọc
 * Popover; bản trong measure row chỉ giữ đúng kích thước.
 *
 * Cột "Mã đơn hàng" có `sorter: true`, và component này chính là `title` của cột
 * đó — nên dù Popover portal nội dung ra `document.body` (ngoài DOM của `<th>`),
 * React vẫn nổi bọt (bubble) sự kiện click theo CÂY REACT (nơi Popover được khai
 * báo), không theo cây DOM. Bấm bất kỳ nút nào trong panel hành động (Gắn thẻ, Bỏ
 * chọn, Đi đơn hàng loạt...) do đó cũng kích hoạt luôn `onClick` sort của `<th>`,
 * âm thầm đổi `sort_by=channel_order_number` trên URL — list đổi sang một query
 * key khác (chưa có cache) nên chớp trắng rồi hiện lại theo thứ tự alphabet thay
 * vì giữ nguyên vị trí. Chặn bằng `stopPropagation` trên wrapper ngoài cùng.
 */
const OrderCodeSelectionHeader: React.FC<OrderCodeSelectionHeaderProps> = ({ selectedCount, panel, registerClosePanel }) => {
  const [inMeasureRow, setInMeasureRow] = useState(false);
  const inMeasureRowRef = useRef(false);
  // Ẩn gắn với số đơn lúc ẩn: chọn thêm/bớt đơn là panel tự hiện lại, không cần effect.
  const [hiddenAtCount, setHiddenAtCount] = useState<number | null>(null);
  const selectedCountRef = useRef(selectedCount);
  selectedCountRef.current = selectedCount;
  const open = hiddenAtCount !== selectedCount;
  // Neo mặc định trùng mép trái cột sẽ che mất mã đơn hàng — đẩy panel sang phải
  // đúng bằng phần cột còn lại để vẫn đọc được mã đơn của các dòng đã chọn.
  const [offsetX, setOffsetX] = useState(0);

  const measureAnchor = useCallback((node: HTMLSpanElement | null) => {
    const isMeasureRow = Boolean(node?.closest('.ant-table-measure-row'));
    inMeasureRowRef.current = isMeasureRow;
    setInMeasureRow(isMeasureRow);
    if (!node) return;
    const headerCell = node.closest('th');
    if (!headerCell) return;
    const anchorLeft = node.getBoundingClientRect().left;
    const gap = headerCell.getBoundingClientRect().right - anchorLeft;
    if (gap <= 0) return;
    // TCR-2504 — màn hẹp (điện thoại): đẩy hết bề rộng cột sẽ tràn mép phải, antd tự dời panel
    // sang trái và che mất cột checkbox (panel không tự đóng ⇒ không tick thêm/bỏ đơn được).
    // Chỉ lùi lại vừa đủ để nằm trong màn, không bao giờ lấn sang trái mép badge.
    const room = window.innerWidth - anchorLeft - PANEL_OUTER_WIDTH - PANEL_GAP;
    setOffsetX(Math.max(0, Math.min(Math.round(gap) + PANEL_GAP, Math.floor(room))));
  }, []);

  const closePanel = useCallback(() => setHiddenAtCount(selectedCountRef.current), []);

  useEffect(() => {
    if (!registerClosePanel || inMeasureRowRef.current) return undefined;
    registerClosePanel(closePanel);
    return () => registerClosePanel(() => undefined);
  }, [closePanel, registerClosePanel]);

  const badge = (
    <span
      data-testid="order-code-selected-count"
      onClick={() => setHiddenAtCount(null)}
      style={{
        cursor: 'pointer',
        display: 'inline-block',
        minWidth: 18,
        padding: '0 5px',
        borderRadius: 9,
        background: '#a4193d',
        color: '#fff',
        fontSize: 11,
        lineHeight: '18px',
        textAlign: 'center',
        // Cột hẹp: flex co badge lại làm số "20" rớt dòng (TCR-2264 QA 13/10) — giữ nguyên một dòng.
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {selectedCount}
    </span>
  );

  return (
    <span
      ref={measureAnchor}
      // Chặn click trong panel (kể cả nội dung Popover đã portal ra body) nổi
      // bọt lên onClick-sort của `<th>` — xem giải thích đầy đủ ở JSDoc trên.
      onClick={(event) => event.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      {inMeasureRow ? badge : (
        <Popover
          open={open}
          trigger={[]}
          zIndex={PANEL_Z_INDEX}
          placement="bottomLeft"
          arrow={false}
          align={{ offset: [offsetX, 4] }}
          styles={{ body: { padding: 10 } }}
          content={panel}
        >
          {badge}
        </Popover>
      )}
      <span style={{ whiteSpace: 'nowrap' }}>Mã đơn hàng</span>
    </span>
  );
};

export default OrderCodeSelectionHeader;
